// src/components/PreLoader.js
import React, { useMemo } from 'react';
import Logo from '../assets/logo.png'; // Adjust path as needed

const PreLoader = () => {
  const brandName = useMemo(() => 'DiceAce21', []);
  const loadingMessages = useMemo(() => [
    'Loading premium betting experience...',
    'Preparing your odds and markets...',
    'Getting live matches ready...',
    'Almost there, placing bets soon...',
    'Securing your gaming environment...'
  ], []);

  // Get a random loading message
  const randomMessage = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * loadingMessages.length);
    return loadingMessages[randomIndex];
  }, [loadingMessages]);

  const loadingDots = useMemo(() => [
    { delay: '0ms', color: 'bg-blue-500' },
    { delay: '150ms', color: 'bg-cyan-500' },
    { delay: '300ms', color: 'bg-blue-600' }
  ], []);

  return (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center z-50"
      role="alert"
      aria-live="assertive"
      aria-label="Loading DiceAce21"
    >
      <div className="text-center px-4 max-w-md">
        {/* Animated Logo Container */}
        <div className="relative w-40 h-40 mb-8 mx-auto">
          {/* Outer glow ring */}
          <div 
            className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 via-blue-500/10 to-cyan-500/20 animate-pulse"
            aria-hidden="true"
          />
          
          {/* Outer ring */}
          <div 
            className="absolute inset-0 w-full h-full border-4 border-gray-800 rounded-full"
            aria-hidden="true"
          />
          
          {/* Spinning ring - primary animation */}
          <div 
            className="absolute inset-0 w-full h-full border-4 border-transparent border-t-blue-500 border-r-cyan-500 rounded-full animate-spin"
            style={{ animationDuration: '1.5s' }}
            aria-hidden="true"
          />
          
          {/* Secondary spinning ring - opposite direction */}
          <div 
            className="absolute inset-3 w-34 h-34 border-4 border-transparent border-b-blue-600 border-l-cyan-400 rounded-full animate-spin"
            style={{ animationDuration: '2s', animationDirection: 'reverse' }}
            aria-hidden="true"
          />
          
          {/* Logo Container */}
          <div 
            className="absolute inset-0 flex items-center justify-center z-10"
            aria-hidden="true"
          >
            <div className="relative w-35 h-35">
              {/* Logo with pulse effect */}
              <img 
                src={Logo}
                alt={`${brandName} Logo`}
                className="w-full h-full object-contain animate-pulse"
                style={{ animationDuration: '2s' }}
              />
              
              {/* Inner glow effect */}
              <div 
                className="absolute inset-0 bg-blue-500/10 rounded-full blur-md animate-ping"
                style={{ animationDuration: '3s' }}
                aria-hidden="true"
              />
            </div>
          </div>
          
          {/* Floating dots around the logo */}
          {[0, 90, 180, 270].map((angle, index) => (
            <div
              key={index}
              className="absolute w-3 h-3 bg-blue-500 rounded-full animate-bounce"
              style={{
                top: `${50 + 45 * Math.sin(angle * Math.PI / 180)}%`,
                left: `${50 + 45 * Math.cos(angle * Math.PI / 180)}%`,
                animationDelay: `${index * 0.2}s`,
                animationDuration: '1.5s'
              }}
              aria-hidden="true"
            />
          ))}
        </div>
        
        {/* Brand Name and Loading Text */}
        <div className="space-y-6">
          <div>
            <div className="text-white text-3xl font-bold mb-2 tracking-wide bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {brandName}
            </div>
            <div className="text-gray-400 text-base animate-fade-in">
              {randomMessage}
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="relative w-64 h-2 bg-gray-800 rounded-full overflow-hidden mx-auto">
            <div 
              className="absolute h-full bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-600 animate-progress rounded-full"
              style={{ animationDuration: '1.8s', animationIterationCount: 'infinite' }}
              aria-hidden="true"
            />
            {/* Progress glow */}
            <div 
              className="absolute h-full bg-gradient-to-r from-blue-500/30 via-cyan-500/30 to-blue-600/30 animate-progress rounded-full blur-sm"
              style={{ animationDuration: '1.8s', animationIterationCount: 'infinite', animationDelay: '0.1s' }}
              aria-hidden="true"
            />
          </div>
          
          {/* Loading dots with different colors */}
          <div className="flex justify-center gap-3 mt-6" aria-hidden="true">
            {loadingDots.map((dot, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full ${dot.color} animate-bounce`}
                style={{ 
                  animationDelay: dot.delay, 
                  animationDuration: '0.8s',
                  boxShadow: '0 0 10px currentColor'
                }}
              />
            ))}
          </div>
        
        </div>
        
        {/* Accessibility announcement */}
        <div className="sr-only" role="status">
          {brandName} is loading. {randomMessage}
        </div>
      </div>
      
      {/* Add custom animation keyframes */}
      <style jsx>{`
        @keyframes progress {
          0% { 
            transform: translateX(-100%); 
            width: 30%;
          }
          50% { 
            transform: translateX(0%); 
            width: 100%;
          }
          100% { 
            transform: translateX(100%); 
            width: 30%;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-progress {
          animation: progress 1.8s ease-in-out infinite;
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PreLoader;